import { useState, useMemo } from 'react';
import { Users, Plus, ShieldAlert, AlertCircle, Shield, UserCog, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { UserList } from '../../components/users/UserList';
import { UserForm } from '../../components/users/UserForm';
import { UserSessions } from '../../components/users/UserSessions';
import { UserStatusModal } from '../../components/users/UserStatusModal';
import { TransferOwnershipModal } from '../../components/users/TransferOwnershipModal';
import { Modal } from '../../components/ui/Modal';
import { useUsers, type UserWithDetails } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { isAuditRole } from '../../utils/roles';

type ActiveTab = 'users' | 'auditors';

export function UsersPage() {
  const { users, currentUser, loading, error, addUser, updateUser, toggleUserStatus, removeAuditor, reactivateAuditor } = useUsers();
  const { can, isAdmin, roleLabel } = usePermissions();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('users');
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [showSessions, setShowSessions] = useState(false);
  const [userToToggle, setUserToToggle] = useState<UserWithDetails | null>(null);

  // Vérifier l'accès à la page - basé sur le rôle admin ou permission manage_users
  const canManageUsers = can('manage_users') || isAdmin;

  // Séparer utilisateurs et auditeurs
  const regularUsers = useMemo(() => users.filter(u => !isAuditRole(u.role)), [users]);
  const auditorUsers = useMemo(() => users.filter(u => isAuditRole(u.role)), [users]);
  const filteredUsers = activeTab === 'users' ? regularUsers : auditorUsers;

  // Accès refusé si pas admin et pas de permission
  if (!canManageUsers) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
        <div className="card p-8 text-center">
          <ShieldAlert className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Accès restreint</h2>
          <p className="text-muted-foreground">
            En tant que <strong>{roleLabel}</strong>, vous n'avez pas accès à la gestion des utilisateurs.
          </p>
        </div>
      </div>
    );
  }

  const handleEditUser = (user: UserWithDetails) => {
    setSelectedUser(user);
  };

  const handleViewSessions = (user: UserWithDetails) => {
    setSelectedUser(user);
    setShowSessions(true);
  };

  const handleToggleStatus = async (user: UserWithDetails) => {
    setUserToToggle(user);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggle) {
      if (isAuditRole(userToToggle.role)) {
        // Pour les auditeurs : retirer ou réactiver via les endpoints dédiés
        if (userToToggle.status === 'active') {
          await removeAuditor(userToToggle.id);
        } else {
          await reactivateAuditor(userToToggle.id);
        }
      } else {
        await toggleUserStatus(userToToggle.id, userToToggle.status);
      }
      setUserToToggle(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => navigate('/activity-logs')}>
                <Activity className="h-4 w-4 mr-2" />
                Logs
              </Button>
              <Button variant="outline" onClick={() => setShowTransferModal(true)}>
                <UserCog className="h-4 w-4 mr-2" />
                Transférer
              </Button>
            </>
          )}
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowNewUserModal(true)}
          >
            {activeTab === 'auditors' ? 'Ajouter un expert' : 'Nouvel Utilisateur'}
          </Button>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          <Users className="w-4 h-4" />
          Utilisateurs
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{regularUsers.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('auditors')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'auditors'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          <Shield className="w-4 h-4" />
          Experts
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{auditorUsers.length}</span>
        </button>
      </div>

      <Card title={activeTab === 'auditors' ? 'Experts de l\'organisation' : 'Liste des utilisateurs'} icon={activeTab === 'auditors' ? Shield : Users}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <UserList
          users={filteredUsers}
          currentUserId={currentUser?.id}
          onEdit={handleEditUser}
          onToggleStatus={handleToggleStatus}
          onViewSessions={handleViewSessions}
          loading={loading}
        />
      </Card>

      <Modal
        isOpen={showNewUserModal}
        onClose={() => setShowNewUserModal(false)}
        title={activeTab === 'auditors' ? 'Ajouter un expert' : 'Nouvel Utilisateur'}
      >
        <UserForm
          forceRole={activeTab === 'auditors' ? 'audit' : undefined}
          onSubmit={async (data) => {
            await addUser(data);
            setShowNewUserModal(false);
          }}
          onCancel={() => setShowNewUserModal(false)}
        />
      </Modal>

      {selectedUser && (
        <Modal
          isOpen={!!selectedUser && !showSessions}
          onClose={() => setSelectedUser(null)}
          title="Modifier l'utilisateur"
        >
          <UserForm
            user={selectedUser}
            onSubmit={async (data) => {
              await updateUser(selectedUser.id, data);
              setSelectedUser(null);
            }}
            onCancel={() => setSelectedUser(null)}
          />
        </Modal>
      )}

      {selectedUser && (
        <Modal
          isOpen={showSessions}
          onClose={() => {
            setShowSessions(false);
            setSelectedUser(null);
          }}
          title={`Sessions - ${selectedUser.name}`}
        >
          <UserSessions userId={selectedUser.id} />
        </Modal>
      )}

      {userToToggle && (
        <UserStatusModal
          isOpen={!!userToToggle}
          onClose={() => setUserToToggle(null)}
          onConfirm={handleConfirmToggleStatus}
          user={userToToggle}
        />
      )}

      {showTransferModal && (
        <TransferOwnershipModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          users={users}
          onSuccess={() => {
            setShowTransferModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}